import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from './Navigation';
import SEO from './SEO';

const QuoteFormUpdated = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    subject: 'I need a spare car key',
    carYear: '',
    carMake: location.state?.selectedBrand || '',
    carModel: '',
    regoNumber: '',
    vinNumber: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // Human verification state
  const [captchaNumbers, setCaptchaNumbers] = useState({ num1: 0, num2: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaCorrect, setCaptchaCorrect] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaNumbers({ num1, num2 });
    setCaptchaAnswer('');
    setCaptchaCorrect(false);
  };

  const handleCaptchaChange = (e) => {
    const answer = e.target.value;
    setCaptchaAnswer(answer);
    const correctAnswer = captchaNumbers.num1 + captchaNumbers.num2;
    setCaptchaCorrect(parseInt(answer) === correctAnswer);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create JSON payload for AJAX endpoint
      const formDataToSend = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        subject: formData.subject,
        carYear: formData.carYear,
        carMake: formData.carMake,
        carModel: formData.carModel,
        regoNumber: formData.regoNumber,
        vinNumber: formData.vinNumber,
        message: formData.message,
        _subject: `New Quote Request - ${formData.subject}`,
        _template: 'table',
        _captcha: 'false'
      };

      const response = await fetch('https://formsubmit.co/ajax/sssreeej@yahoo.com', {
        method: 'POST',
        body: JSON.stringify(formDataToSend),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          subject: 'I need a spare car key',
          carYear: '',
          carMake: '',
          carModel: '',
          regoNumber: '',
          vinNumber: '',
          message: ''
        });
        generateCaptcha(); // Reset captcha after successful submission
      } else {
        console.error('Form submission failed:', result);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Check if it's a network/CORS error
      if (error.message === 'Failed to fetch') {
        console.error('FormSubmit.co connection failed. This could be due to:');
        console.error('1. Email not activated - visit /formsubmit-activation.html to activate');
        console.error('2. CORS restrictions on localhost');
        console.error('3. Network connectivity issues');
      }
      
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <SEO 
        title="Request a Quote - Carkey Solutions Perth"
        description="Get a professional quote for your car key needs. Fast, reliable service across Perth and surrounding areas."
        keywords="car key quote, automotive locksmith quote, Perth car key service"
      />
      
      <Navigation />

      {/* Main Content */}
      <div className="container-max px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-2">
              {/* Header */}
              <div className="text-center mb-6">
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Request a <span className="text-carkey-red">Quote</span>
                </h1>
                <p className="text-gray-300">
                  Get your professional car key solution today
                </p>
              </div>

              {/* Form */}
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white font-semibold mb-3 text-lg">
                        Full Name <span className="text-carkey-red">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-semibold mb-3 text-lg">
                        Email Address <span className="text-carkey-red">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white font-semibold mb-3 text-lg">
                        Phone Number <span className="text-carkey-red">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="0400 000 000"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-semibold mb-3 text-lg">
                        Location <span className="text-carkey-red">*</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        required
                        className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="e.g. Perth, Fremantle, Joondalup"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-white font-semibold mb-3 text-lg">
                      Subject <span className="text-carkey-red">*</span>
                    </label>
                    <div className="mb-2">
                      <p className="text-gray-300 text-sm">How can we help you?</p>
                    </div>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm"
                    >
                      <option value="I need a spare car key">I need a spare car key</option>
                      <option value="I've lost my car keys">I've lost my car keys</option>
                      <option value="My car keys have been stolen">My car keys have been stolen</option>
                      <option value="My car key is broken">My car key is broken</option>
                      <option value="Help! I can't get into my car">Help! I can't get into my car</option>
                      <option value="I have an issue with a lock on my car">I have an issue with a lock on my car</option>
                      <option value="My car won't start, is it the key?">My car won't start, is it the key?</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Vehicle Information */}
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-white font-semibold mb-3 text-lg">
                        Car Year <span className="text-carkey-red">*</span>
                      </label>
                      <input
                        type="text"
                        name="carYear"
                        value={formData.carYear}
                        onChange={handleInputChange}
                        required
                        className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="e.g. 2020"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-semibold mb-3 text-lg">
                        Car Make <span className="text-carkey-red">*</span>
                      </label>
                      <input
                        type="text"
                        name="carMake"
                        value={formData.carMake}
                        onChange={handleInputChange}
                        required
                        className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="e.g. Toyota, Honda, Ford"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-semibold mb-3 text-lg">
                        Car Model <span className="text-carkey-red">*</span>
                      </label>
                      <input
                        type="text"
                        name="carModel"
                        value={formData.carModel}
                        onChange={handleInputChange}
                        required
                        className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm"
                        placeholder="e.g. Camry, Civic, Ranger"
                      />
                    </div>
                  </div>

                  {/* Registration Number */}
                  <div>
                    <label className="block text-white font-semibold mb-3 text-lg">
                      Registration Number
                    </label>
                    <input
                      type="text"
                      name="regoNumber"
                      value={formData.regoNumber}
                      onChange={handleInputChange}
                      className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm"
                      placeholder="e.g. ABC123 (optional)"
                    />
                  </div>

                  {/* VIN Number */}
                  <div>
                    <label className="block text-white font-semibold mb-3 text-lg">
                      VIN Number
                    </label>
                    <input
                      type="text"
                      name="vinNumber"
                      value={formData.vinNumber}
                      onChange={handleInputChange}
                      className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm"
                      placeholder="Vehicle Identification Number (optional)"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-white font-semibold mb-3 text-lg">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="6"
                      className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm resize-none"
                      placeholder="Please provide any additional details about your car key needs..."
                    ></textarea>
                  </div>

                  {/* Human Verification */}
                  <div>
                    <label className="block text-white font-semibold mb-3 text-lg">
                      Human Verification <span className="text-carkey-red">*</span>
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2 bg-white/10 rounded-xl px-4 py-3">
                        <span className="text-white text-lg font-bold">{captchaNumbers.num1}</span>
                        <span className="text-white text-lg">+</span>
                        <span className="text-white text-lg font-bold">{captchaNumbers.num2}</span>
                        <span className="text-white text-lg">=</span>
                      </div>
                      <input
                        type="number"
                        value={captchaAnswer}
                        onChange={handleCaptchaChange}
                        required
                        className="w-24 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-center font-bold text-lg focus:border-carkey-red focus:ring-2 focus:ring-carkey-red/20 focus:outline-none transition-all duration-300 backdrop-blur-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="?"
                      />
                      <button
                        type="button"
                        onClick={generateCaptcha}
                        className="px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 transition-colors"
                        title="Generate new calculation"
                      >
                        <i className="fas fa-refresh"></i>
                      </button>
                    </div>
                    {captchaAnswer && !captchaCorrect && (
                      <p className="text-red-400 text-sm mt-2">Incorrect answer. Please try again.</p>
                    )}
                    {captchaCorrect && (
                      <p className="text-green-400 text-sm mt-2">✓ Verification successful!</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="text-center pt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting || !captchaCorrect}
                      className={`group relative bg-gradient-to-r from-carkey-red to-red-600 text-white px-12 py-5 rounded-2xl font-semibold text-xl transition-all duration-500 hover:shadow-2xl hover:shadow-carkey-red/25 hover:-translate-y-1 overflow-hidden ${
                        isSubmitting || !captchaCorrect ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                      <span className="relative flex items-center justify-center">
                        {isSubmitting ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-3"></i>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane mr-3"></i>
                            Submit Quote Request
                          </>
                        )}
                      </span>
                    </button>
                  </div>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="bg-green-500/20 border border-green-500/50 rounded-2xl p-6 text-center">
                      <i className="fas fa-check-circle text-green-400 text-2xl mb-2"></i>
                      <p className="text-green-400 font-semibold">Thank you! Your quote request has been submitted successfully.</p>
                      <p className="text-green-300 text-sm mt-2">We'll get back to you within 1 hour.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-6 text-center">
                      <i className="fas fa-exclamation-circle text-red-400 text-2xl mb-2"></i>
                      <p className="text-red-400 font-semibold">Sorry, there was an error submitting your request.</p>
                      <p className="text-red-300 text-sm mt-2">Please try again or contact us directly:</p>
                      <div className="mt-3 space-y-2">
                        <a href="tel:+61430168669" className="block text-white hover:text-carkey-red transition-colors">
                          <i className="fas fa-phone mr-2"></i>+61 430 168 669
                        </a>
                        <a href="https://wa.me/61430168669" className="block text-white hover:text-carkey-red transition-colors">
                          <i className="fab fa-whatsapp mr-2"></i>WhatsApp Chat
                        </a>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Company Info & Trust Section */}
            <div className="lg:col-span-1">
              {/* Company Header */}
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 mb-6">
                <div className="text-center mb-4">
                  <div className="bg-white rounded-xl p-4 mb-3 inline-block">
                    <img src="/logo/LG.png" alt="Carkey Solutions" className="h-16 w-auto" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    <span className="text-carkey-red">Car</span>key Solutions
                  </h2>
                  <p className="text-gray-300 text-sm">Perth's Leading Automotive Locksmith</p>
                </div>
                
                {/* Company Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center bg-white/10 rounded-xl p-3">
                    <div className="text-carkey-red font-bold text-xl">10+</div>
                    <div className="text-white text-xs">Years Experience</div>
                  </div>
                  <div className="text-center bg-white/10 rounded-xl p-3">
                    <div className="text-carkey-red font-bold text-xl">500+</div>
                    <div className="text-white text-xs">Happy Customers</div>
                  </div>
                </div>

                {/* Contact Info - Only Phone & WhatsApp */}
                <div className="space-y-3">
                  <div className="flex items-center text-white">
                    <i className="fas fa-map-marker-alt text-carkey-red mr-3"></i>
                    <span className="text-sm">70 Division Street, Welshpool 6106</span>
                  </div>
                  <div className="flex items-center text-white">
                    <i className="fas fa-phone text-carkey-red mr-3"></i>
                    <span className="text-sm">+61 430 168 669</span>
                  </div>
                  <div className="flex items-center text-white">
                    <i className="fab fa-whatsapp text-carkey-red mr-3"></i>
                    <a href="https://wa.me/61430168669" className="text-sm hover:text-carkey-red transition-colors">
                      Click to Chat
                    </a>
                  </div>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="bg-gradient-to-br from-carkey-red to-red-600 rounded-2xl p-6 text-center shadow-xl">
                <div className="text-3xl mb-3 text-white">
                  <i className="fas fa-shield-alt"></i>
                </div>
                <div className="text-xl font-bold text-white mb-2">Trusted & Reliable</div>
                <div className="text-sm text-white opacity-90 mb-3">Professional Service Guaranteed</div>
                <div className="flex items-center justify-center space-x-1 text-yellow-300 mb-2">
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                  <i className="fas fa-star"></i>
                </div>
                <div className="text-xs text-white opacity-80">5-Star Rated Service</div>
              </div>

              {/* Service Areas */}
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 mt-6">
                <h3 className="text-white font-bold mb-3">Service Areas</h3>
                <div className="text-gray-300 text-sm space-y-1">
                  <div>• Perth & Surrounding Areas</div>
                  <div>• Fremantle</div>
                  <div>• Joondalup</div>
                  <div>• Rockingham</div>
                  <div>• Canning Vale</div>
                  <div>• And More...</div>
                </div>
              </div>

              {/* Immediate Assistance Section */}
              <div className="bg-gradient-to-r from-carkey-red to-red-600 rounded-2xl p-6 mt-6">
                <h3 className="text-xl font-bold text-white mb-4 text-center">Need Immediate Assistance?</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <i className="fas fa-phone text-white"></i>
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold">Call Us</div>
                      <a href="tel:+61430168669" className="text-white hover:text-gray-200 transition-colors text-sm">
                                                 +61 430 168 669
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <i className="fab fa-whatsapp text-white"></i>
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold">WhatsApp</div>
                                              <a href="https://wa.me/61430168669" className="text-white hover:text-gray-200 transition-colors text-sm">
                        Send Message
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <i className="fas fa-clock text-white"></i>
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold">Response Time</div>
                      <div className="text-white text-sm">Within 1 Hour</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuoteFormUpdated;
